import styled from '@emotion/styled';
import { Icon, Label, Text } from '@britishcouncil/react-admin';
import { Loader, Segment, SegmentGroup } from 'semantic-ui-react';
import { testCentreOverviewStore } from 'store';
import { TestCentreOverviewQueryFilters } from 'store/test-centre-overview/service';

export const TestCentreOverviewSummary = ({
  filters,
}: {
  filters: TestCentreOverviewQueryFilters;
}) => {
  const { data: summary, isFetching } =
    testCentreOverviewStore.service.useGetTestCentreOverviewSummaryQuery({ filters });

  return (
    <SegmentGroup>
      <Segment>
        {isFetching ? (
          <Loader active inline="centered" />
        ) : (
          <S.CardRow>
            <S.Card>
              <S.MetricIcon name="users" color="blue" size="large" circular opaque colored />
              <S.Body>
                <Text color="grey">Planned capacity (LRW)</Text>
                <strong>
                  <Text size="big">{summary?.plannedLrwCapacity ?? 0}</Text>
                </strong>
                <S.SubValues>
                  <S.Status>
                    <Text positive>{summary?.lrwReleasedSeats ?? 0} released</Text>
                  </S.Status>
                  <S.Status>
                    <Text warning>{summary?.lrwUnreleasedSeats ?? 0} unreleased</Text>
                  </S.Status>
                </S.SubValues>
              </S.Body>
            </S.Card>
            <S.Card>
              <S.MetricIcon
                name="check circle"
                color="green"
                size="large"
                circular
                opaque
                colored
              />
              <S.Body>
                <Text color="grey">Registrations</Text>
                <strong>
                  <Text size="big">{summary?.totalRegistrations ?? 0}</Text>
                </strong>
                <S.SubValues>
                  <S.Status>
                    <Text positive>{summary?.confirmedRegistrations ?? 0} confirmed</Text>
                  </S.Status>
                  <S.Status>
                    <Text warning>{summary?.incompleteRegistrations ?? 0} incomplete</Text>
                  </S.Status>
                </S.SubValues>
              </S.Body>
            </S.Card>
            <S.Card>
              <S.MetricIcon name="comments" color="green" size="large" circular opaque colored />
              <S.Body>
                <Text color="grey">Speaking slots</Text>
                <strong>
                  <Text size="big">{summary?.plannedSpeakingSlots ?? 0}</Text>
                </strong>
                <S.SubValues>
                  <S.Status>
                    <Text positive>{summary?.speakingReleasedSlots ?? 0} released</Text>
                  </S.Status>
                  <S.Status>
                    <Text warning>{summary?.speakingUnreleasedSlots ?? 0} unreleased</Text>
                  </S.Status>
                  <S.Status>
                    <Text warning>
                      {summary?.speakingAwaitingConfirmationSlots ?? 0} awaiting confirmation
                    </Text>
                  </S.Status>
                </S.SubValues>
              </S.Body>
            </S.Card>
            <S.Card>
              <S.MetricIcon name="user" color="orange" size="large" circular opaque colored />
              <S.Body>
                <Text color="grey">In-Person examiners coverage</Text>
                <strong>
                  <Text size="big">{summary?.inPersonExaminersCoverage ?? 0}%</Text>
                </strong>
                <S.SubValues>
                  <Label color="orange" size="small">
                    <Icon name="warning sign" />
                    {summary?.unassignedInPersonSpeakingSlots ?? 0} unassigned
                  </Label>
                </S.SubValues>
              </S.Body>
            </S.Card>
          </S.CardRow>
        )}
      </Segment>
    </SegmentGroup>
  );
};

const S = {
  CardRow: styled.div`
    display: flex;
    align-items: stretch;
  `,
  Card: styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    flex: 1;
    padding: 0.5rem 1.5rem;

    & + & {
      border-left: 1px solid rgba(34, 36, 38, 0.1);
    }
  `,
  MetricIcon: styled(Icon)`
    margin-top: 0.625rem;
    flex-shrink: 0;
  `,
  Body: styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  `,
  SubValues: styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
  `,
  Status: styled.span`
    white-space: nowrap;
  `,
};
